import { graphql, PageProps } from 'gatsby';
import React from 'react';
import FranchiseGlobalAbout from '../../components/blocks/franchise-global-about';
import FranchiseReviewsSlider from '../../components/franchise/microsite/reviews-slider';
import type { FranchiseData } from '../../sourcing/source-nodes/sources/franchises';
import DefaultTemplate from '../default';
import commercialResidentialText from './utils/commercial-residential-text';
import sortTestimonialsByCategory from './utils/sort-testimonials';
import transformDynamic from './utils/transform-dynamic';
import useServiceTypeBlockFilter from './utils/use-service-type-block-filter';
import { PageTemplate } from '../../utils/match-page-template';
import { Testimonial } from './types/testimonial';

export type FranchiseServicePageData = Queries.FranchiseServiceTemplateQuery & {
    allFranchiseTestimonial: {
        nodes: Testimonial[]
    }
    franchise: FranchiseData
};

export interface FranchiseServicePageContext {
    slug: string,
    franchiseNumber: number,
    permalink: string,
    id: string,
    testimonialCategories: Record<string, number>,
    templateName: PageTemplate.MicrositeService,
}

const FranchiseService: React.FC<PageProps<FranchiseServicePageData, FranchiseServicePageContext>> = ({ data, pageContext, ...props }) => {
    const { testimonialCategories } = pageContext;

    const testimonials = data?.allFranchiseTestimonial?.nodes || [];

    const sortedTestimonials = sortTestimonialsByCategory(testimonials, testimonialCategories);
    const {
        servicesProvidedReadable,
        servicesProvidedFilterable,
        name,
        activeMarkets,
        address,
        profileExcerpt,
    } = data.franchise as FranchiseData;

    const careerPlug = data?.franchise?.aboutUs?.careerPlugUrl;

    const reparsedData = useServiceTypeBlockFilter(transformDynamic(data, {
        locationName: name,
        locationCity: address?.city,
        locationState: address?.state,
        locationServiceAreas: (activeMarkets || []).map(activeMarket => `${activeMarket.city}, ${activeMarket.state}`)?.join(', '),
        locationCompanyProfile: profileExcerpt,
        jobLink: careerPlug,
        servicesProvidedReadable: (servicesProvidedReadable || []).map(x => x.serviceLine),
        servicesProvidedFilterable: (servicesProvidedFilterable || []).map(x => x.serviceLine),
        servicesForPage: (data.csFranchiseServicePages?.linkedServiceType?.franchiseServiceMapping || '').split(','),
        propertyType: commercialResidentialText(data, 'csFranchiseServicePages'),
    }), 'csFranchiseServicePages') as FranchiseServicePageData;

    if (!reparsedData?.csFranchiseServicePages) return null;

    return (
        <DefaultTemplate
            {...props}
            pageContext={pageContext}
            data={{ csFranchiseServicePages: reparsedData.csFranchiseServicePages }}
        >
            {sortedTestimonials.length > 2 && (
                <FranchiseReviewsSlider slides={sortedTestimonials} />
            )}
            <FranchiseGlobalAbout />
        </DefaultTemplate>
    );
};

export const query = graphql`
    query FranchiseServiceTemplate ($slug: String!, $franchiseNumber: Int!) {
        csFranchiseServicePages(url: { eq: $slug }) {
            title
            url
            seo {
                metaTitle
                metaDescription
                metaImage {
                    url
                }
            }
            linkedServiceType {
                franchiseServiceMapping
            }
            sections {
                __typename
                ... on CsShowBelowIfGroup {
                    linkedServiceType {
                        franchiseServiceMapping
                    }
                }
                ...HeroData
                ...IconBulletsData
                ...MediaContentData
                ...MediacontentFaqsData
                ...ServicesDetailData
                ...SliderData
                ...ServiceAreaData
                ...ServicesWeProvideData
                ...AnchorData
                ...FranchiseFormCtaData
                ...FranchiseFormHeroData
                ...FaqsGridData
            }
        }
        allFranchiseUniqueContent(filter: {
            franchiseNumber: {
                eq: $franchiseNumber
            }
        }) {
            nodes {
                approvedDate
                pageName
                content
                rawContent
            }
        }
        franchise(franchiseNumber: { eq: $franchiseNumber }) {
            ...FranchiseMicrositeData
        }
        allFranchiseTestimonial(filter: { franchiseNumber: { eq: $franchiseNumber }}) {
          nodes {
                ...AllFranchiseTestimonialData
          }
        }
    }
`;

export default FranchiseService;
